'use client'

import type React from 'react'
import {
  createContext,
  useContext,
  useState,
  useMemo,
  useCallback,
} from 'react'
import api, { setApiAuthToken } from '@/api'
import { supabase } from '@/services/supabase'
import { toast } from 'sonner'
import { useAuth } from './auth-context'

interface Organization {
  id: string
  name: string
  role: string
  logo?: string | null
  description?: string
  onboarding?: { team_invites?: boolean }
  billing_type?: string
  public_email?: string
  url?: string
  company_size?: string
  industry?: string
  resdb_access?: boolean
}

interface OrgList {
  id: string
  name: string
  role: string
  logo?: string | null
}

type OrgContextType = {
  organizations: {
    data: OrgList[]
    isLoading: boolean
    isError: boolean
    refetch: () => void
  }
  currentOrg: Organization | null
  isLoading: boolean
  switchOrg: (orgId: string) => Promise<void>
  switchPopup: boolean
  setSwitchPopup: React.Dispatch<React.SetStateAction<boolean>>
}

const OrgContext = createContext<OrgContextType | undefined>(undefined)

export const useOrg = () => {
  const ctx = useContext(OrgContext)
  if (!ctx) throw new Error('useOrg must be used within OrgProvider')
  return ctx
}

export const OrgProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { loading: authLoading, bootstrapData, fetchUserProfile } = useAuth()
  const [switchPopup, setSwitchPopup] = useState(false)

  // Derive organization data from consolidated bootstrap
  const organizationsData = useMemo(() => {
    if (!bootstrapData) return []
    return bootstrapData.organizations.list
  }, [bootstrapData])

  const currentOrgData = useMemo(() => {
    if (!bootstrapData) return null
    const defaultId = bootstrapData.organizations.default_id
    if (!defaultId) return null
    
    const org = bootstrapData.organizations.list.find(o => o.id === defaultId)
    if (!org) return null

    return {
      ...org,
      id: org.id,
      name: org.name,
      role: org.role,
      logo: org.logo
    } as Organization
  }, [bootstrapData])

  const refetch = useCallback(async () => {
    // WORLD-CLASS: Soft Refetch 
    await fetchUserProfile().catch(err => console.error('[ORG] Refetch failed:', err))
  }, [fetchUserProfile])

  const switchOrg = useCallback(
    async (orgId: string) => {
      try {
        setSwitchPopup(false)

        // Call backend to switch org (updates user's app_metadata)
        await api.post(`/college/org/switch/${orgId}`)

        // Refresh session to get new JWT with updated org_id/role claims
        const { data: refreshData, error: refreshError } =
          await supabase.auth.refreshSession()
        if (!refreshError && refreshData.session) {
          setApiAuthToken(refreshData.session.access_token)
        }

        window.location.replace('/')
      } catch (err) {
        toast.error('Failed to switch organization')
        throw err
      }
    },
    // eslint-disable-next-line @tanstack/query/no-unstable-deps
    [bootstrapData]
  )

  const value = useMemo<OrgContextType>(
    () => ({
      organizations: {
        data: organizationsData,
        isLoading: authLoading && !bootstrapData,
        isError: false,
        refetch,
      },
      currentOrg: currentOrgData,
      isLoading: authLoading,
      switchOrg,
      switchPopup,
      setSwitchPopup,
    }),
    [
      organizationsData,
      currentOrgData,
      authLoading,
      bootstrapData,
      refetch,
      switchOrg,
      switchPopup,
    ]
  )

  return <OrgContext.Provider value={value}>{children}</OrgContext.Provider>
}
